import { IReactionDisposer, IReactionOptions, reaction } from 'mobx';
import { task, WithoutPromise } from 'mobx-task';
import { TaskFunc, TaskMethods, TaskStatus } from 'mobx-task/lib/task';
import isNil from 'ramda/src/isNil';
import { Observable } from 'rxjs';

interface TaskStateWithError<A extends any[], R> {
  /**
   * The status (resolved, rejected, pending)
   */
  readonly state: TaskStatus;
  /**
   * Convenience getter for `state === 'pending'`.
   */
  readonly pending: boolean;
  /**
   * Convenience getter for `state === 'resolved'`.
   */
  readonly resolved: boolean;
  /**
   * Convenience getter for `state === 'rejected'`.
   */
  readonly rejected: boolean;
  /**
   * The last arguments passed to the task.
   */
  readonly args: A;
  /**
   * The result of the last invocation.
   */
  readonly result?: WithoutPromise<R>;
  /**
   * The error of the last failed invocation.
   */
  readonly error?: Error;
}

declare type Task<A extends any[], R> = TaskFunc<A, WithoutPromise<R>> &
  TaskStateWithError<A, R> &
  TaskMethods<A, R>;
export declare type AnyTask = Task<any[], any>;
export declare type ResultAwareTask<R> = Task<any[], R>;

// TODO merge all of these when we update to TypeScript 4
type NoArgWorker<R> = () => Observable<R>;
type OneArgWorker<T1, R> = (first: T1) => Observable<R>;
type TwoArgWorker<T1, T2, R> = (first: T1, second: T2) => Observable<R>;
type ThreeArgWorker<T1, T2, T3, R> = (first: T1, second: T2, third: T3) => Observable<R>;
type FourArgWorker<T1, T2, T3, T4, R> = (
  first: T1,
  second: T2,
  third: T3,
  fourth: T4,
) => Observable<R>;

// TODO TypeScript does not even complain when the arguments are not passed/not the right type
// Find a solution or simply use ResultAwareTask everywhere
export interface NoArgTask<U> extends ResultAwareTask<U> {
  (): U;
}

export interface OneArgTask<T1, U> extends ResultAwareTask<U> {
  (first: T1): U;
}

export interface TwoArgTask<T1, T2, U> extends ResultAwareTask<U> {
  (first: T1, second: T2): U;
}

export interface ThreeArgTask<T1, T2, T3, U> extends ResultAwareTask<U> {
  (first: T1, second: T2, third: T3): U;
}

export interface FourArgTask<T1, T2, T3, T4, U> extends ResultAwareTask<U> {
  (first: T1, second: T2, third: T3, fourth: T4): U;
}

// TODO change this when we update to TypeScript 4
/**
 * Tasks are created with the "resolved" state. In that case, to know if they were successfully executed or not,
 * rely on result.
 * @param worker
 */
const rxTaskImpl = (worker: (...args: any[]) => Observable<any>): Task<any[], any> =>
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  task.resolved(async (...args: any[]) => await worker(...args).toPromise(), {
    swallow: true,
  }) as Task<any[], any>;

export const rxTask: RxTask = rxTaskImpl as any;

interface RxTask {
  <U>(worker: NoArgWorker<U>, options?: Record<string, any>): NoArgTask<U>;
  <T1, U>(worker: OneArgWorker<T1, U>, options?: Record<string, any>): OneArgTask<T1, U>;
  <T1, T2, U>(worker: TwoArgWorker<T1, T2, U>, options?: Record<string, any>): TwoArgTask<
    T1,
    T2,
    U
  >;
  <T1, T2, T3, U>(
    worker: ThreeArgWorker<T1, T2, T3, U>,
    options?: Record<string, any>,
  ): ThreeArgTask<T1, T2, T3, U>;
  <T1, T2, T3, T4, U>(
    worker: FourArgWorker<T1, T2, T3, T4, U>,
    options?: Record<string, any>,
  ): FourArgTask<T1, T2, T3, T4, U>;
}

export const onSuccessReaction = <T>(
  t: ResultAwareTask<T>,
  callback?: (result: T) => void,
  opts?: IReactionOptions,
): IReactionDisposer | null =>
  isNil(t)
    ? null
    : reaction(
        () => t.result as T,
        (result: T) => {
          if (t.resolved && !isNil(result)) {
            callback?.(result);
          }
        },
        opts,
      );

export const onErrorReaction = <T extends Error>(
  t: AnyTask,
  callback?: (error?: T) => void,
  opts?: IReactionOptions,
): IReactionDisposer | null =>
  isNil(t)
    ? null
    : reaction(
        () => t.rejected,
        () => {
          if (!isNil(t.error)) {
            callback?.(t.error as T); // TODO remove casting when tasks are typed with the right error type
          }
        },
        opts,
      );

export const onPendingReaction = (
  t: AnyTask,
  callback?: (pending: boolean) => void,
  opts?: IReactionOptions,
): IReactionDisposer | null =>
  isNil(t)
    ? null
    : reaction(
        () => t.pending,
        () => {
          callback?.(t.pending);
        },
        opts,
      );



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/common/src/helper/mobx-tasks.ts